import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { useApp } from "../lib/AppContext";
import { useFetch } from "../lib/useFetch";
import { fmt, ts } from "../lib/api";
import { Panel, Empty } from "../components/common";

const MKTS = ["ALL", "BURSA", "US", "HK", "SG"];
const Act = ({ a }) => <span className={`mono rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${a === "BUY" ? "bg-emerald-50 text-emerald-700" : a === "SELL" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>{a}</span>;

function Filter({ v, set, id }) {
  return <div className="flex flex-wrap gap-1">{MKTS.map((m) => <button key={m} onClick={() => set(m)} data-testid={`${id}-${m.toLowerCase()}`} className={`mono rounded-sm border px-2 py-0.5 text-[10px] ${v === m ? "border-blue-600 bg-blue-600 text-white" : "border-[#E2E8F0] text-slate-600"}`}>{m}</button>)}</div>;
}

function TodaySignals() {
  const { data } = useFetch("/member/signals", 120000);
  const [m, setM] = useState("ALL");
  const rows = (data?.signals || []).filter((r) => m === "ALL" || r.exchange === m);
  return (
    <Panel title="Signal hari ini" testid="member-today" right={<Filter v={m} set={setM} id="member-filter" />}>
      {!data ? <Loader2 className="h-4 w-4 animate-spin" /> : !rows.length ? <Empty>Tiada signal — AI memilih NO TRADE</Empty> : (
        <div className="custom-scrollbar overflow-x-auto"><table className="tbl w-full">
          <thead><tr><th>Saham</th><th>Signal</th><th>Masuk</th><th>Stop-loss</th><th>TP1 / TP2 / TP3</th><th>R:R</th><th>Skor</th><th>Sebab</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.symbol} data-testid={`member-signal-${r.symbol}`}><td className="mono">{r.symbol}<div className="text-[10px] text-slate-500">{r.name} · {r.exchange}</div></td><td><Act a={r.action} /></td>
              <td className="mono">{r.action === "BUY" ? fmt(r.entry, r.entry < 10 ? 3 : 2) : "—"}</td><td className="mono text-red-600">{r.action === "BUY" ? fmt(r.stop, r.stop < 10 ? 3 : 2) : "—"}</td>
              <td className="mono text-emerald-600">{r.action === "BUY" ? [r.tp1, r.tp2, r.tp3].map((x) => fmt(x, x < 10 ? 3 : 2)).join(" / ") : "—"}</td>
              <td className="mono">{r.action === "BUY" ? r.rr : "—"}</td><td className="mono">{r.score}</td><td className="max-w-xs text-[11px] text-slate-600">{(r.reasons || []).slice(0, 2).join("; ")}</td></tr>))}
          </tbody></table></div>
      )}
      <p className="mt-2 text-[10px] text-slate-500" data-testid="member-data-note">{data?.data_note} Dikemas kini {ts(data?.scanned_at)}.</p>
    </Panel>
  );
}

function History() {
  const [m, setM] = useState("ALL");
  const { data } = useFetch(`/member/signals/history${m === "ALL" ? "" : `?market=${m}`}`, 0, [m]);
  return (
    <Panel title="Sejarah signal & apa jadi selepas itu" className="mt-4" testid="member-history" right={<Filter v={m} set={setM} id="member-hist-filter" />}>
      {!data ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <>
          <div className="mono mb-2 flex gap-4 text-[11px]" data-testid="member-history-stats">{["BUY", "SELL"].map((a) => <span key={a}>{a}: {data.stats?.[a]?.count} · betul {data.stats?.[a]?.right_pct}%</span>)}</div>
          <div className="custom-scrollbar max-h-[420px] overflow-auto"><table className="tbl w-full">
            <thead><tr><th>Masa</th><th>Saham</th><th>Signal</th><th>Harga</th><th>Keputusan</th></tr></thead>
            <tbody>{data.rows.slice(0, 120).map((r, i) => (
              <tr key={i}><td className="mono text-[11px]">{ts(typeof r.time === "number" ? r.time * 1000 : r.time)}</td><td className="mono">{r.symbol}</td><td><Act a={r.action} /></td><td className="mono">{fmt(r.price, 3)}</td>
                <td className={`mono text-[11px] ${r.verdict === "RIGHT" ? "text-emerald-600" : r.verdict === "WRONG" ? "text-red-600" : "text-slate-500"}`}>{r.verdict}</td></tr>))}
            </tbody></table></div>
          <p className="mt-2 text-[10px] text-slate-500">{data.note}</p>
        </>
      )}
    </Panel>
  );
}

export default function MemberDashboard() {
  const { user, logout } = useApp();
  const nav = useNavigate();
  useEffect(() => { if (user === false) nav("/login?next=/member"); else if (user && user.role !== "member") nav("/"); }, [user, nav]);
  if (!user) return <div className="grid h-screen place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  return (
    <div className="grain min-h-screen bg-white text-slate-900" data-testid="member-dashboard">
      <header className="flex items-center justify-between border-b border-[#E2E8F0] px-4 py-3">
        <div className="flex items-center gap-2 font-display text-sm font-extrabold"><img src="/logo.svg" alt="DMI" className="h-7 w-7" />DMI <span className="text-blue-600">SIGNAL</span></div>
        <div className="flex items-center gap-3 text-xs text-slate-600"><span data-testid="member-name">{user.name}</span>
          <button onClick={async () => { await logout(); nav("/welcome"); }} className="flex items-center gap-1 rounded-sm border border-[#E2E8F0] px-2 py-1 hover:border-blue-600" data-testid="member-logout"><LogOut className="h-3.5 w-3.5" />Log keluar</button></div>
      </header>
      <main className="mx-auto max-w-6xl p-4">
        <p className="mb-4 text-[11px] text-slate-500" data-testid="member-disclaimer">Akses baca sahaja. Signal untuk maklumat, bukan nasihat pelaburan. Tiada keuntungan dijamin.</p>
        <TodaySignals /><History />
      </main>
    </div>
  );
}
